

import { useEffect, useState } from "react";
import { getFirebaseServices } from "@/lib/firebase/client";
import { getAuth } from "firebase/auth";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

const REASONS = [
  { value: "bug", label: "Bug report" },
  { value: "billing", label: "Billing issue" },
  { value: "account", label: "Account support" },
  { value: "feature", label: "Feature request" },
  { value: "other", label: "Other" },
];

export default function SupportBox() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState(REASONS[0].value);
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(true);
  const [status, setStatus] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const services = getFirebaseServices();
    if (!services) return;
    const auth = getAuth();
    const user = auth.currentUser;
    if (user && user.email) {
      setEmail(user.email);
      if (!name && user.displayName) setName(user.displayName);
    }
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) {
      setStatus("Please enter a message describing your issue.");
      return;
    }
    setSubmitting(true);
    setStatus(null);

    try {
      const services = getFirebaseServices();
      if (!services) throw new Error("Firebase not configured");
      const auth = getAuth();
      const user = auth.currentUser;

      const ticket = {
        name: name.trim() || (user?.displayName ?? ""),
        email: user?.email ?? email,
        reason,
        message: message.trim(),
        consentToEmail: !!consent,
        status: "open",
        createdAt: serverTimestamp(),
        createdBy: user?.uid ?? null,
      } as any;

      await addDoc(collection(services.db, "support"), ticket);
      const successMessage = "Thanks, your support ticket was submitted. We'll follow up to the email address on your account.";
      setStatus(successMessage);
      setMessage("");
      setSubmitted(true);
      setOpen(false);
    } catch (err: any) {
      console.error("Support submit error", err);
      setStatus(err?.message || "Failed to submit support ticket");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mt-6 rounded-3xl border border-white/10 bg-zinc-900/70 p-5 text-white">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold">Support</h3>
          <p className="mt-1 text-sm text-white/60">Contact support for bugs, billing, or account help.</p>
        </div>
        <div>
          {!open ? (
            <button
              onClick={() => { setOpen(true); setSubmitted(false); setStatus(null); }}
              className="rounded-2xl bg-white/10 px-3 py-2 text-sm font-semibold"
            >
              Open Ticket
            </button>
          ) : (
            <button
              onClick={() => setOpen(false)}
              className="rounded-2xl bg-white/10 px-3 py-2 text-sm font-semibold"
            >
              Close
            </button>
          )}
        </div>
      </div>

      {submitted && status && (
        <div className="mt-4 rounded-2xl bg-green-900/40 p-3 text-sm text-lime-200">
          {status}
        </div>
      )}

      {open && (
        <form onSubmit={submit} className="mt-4 space-y-3">
          <div>
            <label className="text-xs text-white/60">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="mt-1 w-full rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white"
            />
          </div>

          <div>
            <label className="text-xs text-white/60">Email (read only)</label>
            <input
              value={email}
              readOnly
              className="mt-1 w-full rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/50"
            />
          </div>

          <div>
            <label className="text-xs text-white/60">Reason</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-1 w-full rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white"
            >
              {REASONS.map((r) => (
                <option key={r.value} value={r.value} className="bg-zinc-900 text-white">
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs text-white/60">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              placeholder="Describe your issue or question"
              className="mt-1 w-full rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="consent"
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="h-4 w-4"
            />
            <label htmlFor="consent" className="text-sm text-white/70">
              I consent to be contacted by email about this ticket.
            </label>
          </div>

          <div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-2xl bg-lime-400 px-4 py-2 text-sm font-semibold text-black disabled:opacity-60"
            >
              {submitting ? "Submitting..." : "Send support request"}
            </button>
          </div>

          {status && <p className="text-sm text-white/70">{status}</p>}
        </form>
      )}
    </section>
  );
}
